'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { usePalette } from '@/contexts/PaletteContext'
import { getArtistInitials, getArtistColorVar } from '@/lib/artistAvatar'

// Helper to get computed CSS variable value
function useComputedColor(cssVar: string): string {
  const [color, setColor] = useState('#888888')

  useEffect(() => {
    const computed = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim()
    if (computed) setColor(computed)
  }, [cssVar])

  return color
}

const artists = [
  {
    id: 1,
    name: 'Yayoi Kusama',
    nationality: 'Japanese',
    born: 1929,
    died: null,
    movements: ['Pop Art', 'Minimalism'],
    tier: 'Blue-chip',
    careerStage: 'Established',
    medium: 'Paintings, Sculpture',
    trend: 18.4,
    trendConfidence: 94,
    colorVar: '--accent-primary',
    marketCap: '$2.4B',
    marketCapConfidence: 96,
    avgHammer: '$1.8M',
    avgHammerConfidence: 98,
    recordSale: '$28.3M',
    totalLots: '4,892',
    buyInRate: '12%',
    buyInConfidence: 91,
    sellThrough: '88%',
    sellThroughConfidence: 91,
    overallConfidence: 95,
    // Price history with individual works as data points
    priceHistory: [
      { date: '2019-03', work: 'Infinity Nets (TWAQ)', value: 4.2, reserve: 3.5, hammer: 4.2 },
      { date: '2019-11', work: 'Pumpkin (Yellow)', value: 2.8, reserve: 2.0, hammer: 2.8 },
      { date: '2020-06', work: 'Dots Obsession', value: 1.6, reserve: 1.2, hammer: 1.6 },
      { date: '2021-05', work: 'Infinity Nets (OQPW)', value: 7.8, reserve: 5.0, hammer: 7.8 },
      { date: '2021-11', work: 'Pumpkin (T.W.)', value: 5.1, reserve: 3.5, hammer: 5.1 },
      { date: '2022-05', work: 'Infinity Nets (QRTW)', value: 10.5, reserve: 7.0, hammer: 10.5 },
      { date: '2022-11', work: 'Yellow Pumpkin', value: 6.2, reserve: 4.5, hammer: 6.2 },
      { date: '2023-05', work: 'Infinity Nets (ABCD)', value: 8.4, reserve: 6.0, hammer: 8.4 },
      { date: '2023-11', work: 'Dots Obsession', value: 2.1, reserve: 1.5, hammer: 2.1 },
      { date: '2024-01', work: 'Infinity Nets (QRTWE)', value: 8.9, reserve: 6.0, hammer: 8.9 },
    ],
    recentSales: [
      {
        work: 'Infinity Nets (QRTWE)',
        house: "Christie's",
        reserve: '$6.0M',
        hammer: '$8.9M',
        premium: '+48%',
        date: 'Jan 2024',
        confidence: 99,
        provenance: [
          { owner: 'Private Collection, Tokyo', period: '2005-2024' },
          { owner: 'Gallery Ota Fine Arts', period: '2002-2005' },
          { owner: 'Artist Studio', period: '1998-2002' },
        ],
        lotNotes: 'Accompanied by certificate of authenticity from the artist studio',
      },
      {
        work: 'Pumpkin (T.W.B)',
        house: "Sotheby's",
        reserve: '$3.0M',
        hammer: '$4.2M',
        premium: '+40%',
        date: 'Dec 2023',
        confidence: 98,
        provenance: [
          { owner: 'European Private Collection', period: '2010-2023' },
          { owner: 'Victoria Miro Gallery, London', period: '2008-2010' },
        ],
        lotNotes: 'Exhibited at Tate Modern retrospective, 2012',
      },
      {
        work: 'Dots Obsession',
        house: 'Phillips',
        reserve: '$1.5M',
        hammer: '$2.1M',
        premium: '+40%',
        date: 'Nov 2023',
        confidence: 97,
        provenance: [
          { owner: 'Private Collection, Hong Kong', period: '2015-2023' },
          { owner: 'David Zwirner Gallery', period: '2012-2015' },
        ],
        lotNotes: null,
      },
    ],
    similarArtists: [
      { name: 'Takashi Murakami', similarity: 92 },
      { name: 'Yoshitomo Nara', similarity: 88 },
      { name: 'Kaws', similarity: 84 },
    ],
  },
  {
    id: 2,
    name: 'Jean-Michel Basquiat',
    nationality: 'American',
    born: 1960,
    died: 1988,
    movements: ['Neo-Expressionism', 'Street Art'],
    tier: 'Blue-chip',
    careerStage: 'Historical',
    medium: 'Paintings, Drawings',
    trend: 12.1,
    trendConfidence: 96,
    colorVar: '--accent-secondary',
    marketCap: '$4.1B',
    marketCapConfidence: 97,
    avgHammer: '$3.2M',
    avgHammerConfidence: 99,
    recordSale: '$110.5M',
    totalLots: '2,341',
    buyInRate: '8%',
    buyInConfidence: 94,
    sellThrough: '92%',
    sellThroughConfidence: 94,
    overallConfidence: 97,
    priceHistory: [
      { date: '2019-05', work: 'Untitled (Yellow)', value: 18.5, reserve: 15.0, hammer: 18.5 },
      { date: '2019-11', work: 'Dustheads Study', value: 8.2, reserve: 6.0, hammer: 8.2 },
      { date: '2020-10', work: 'Head', value: 12.4, reserve: 10.0, hammer: 12.4 },
      { date: '2021-05', work: 'In This Case', value: 93.1, reserve: 50.0, hammer: 93.1 },
      { date: '2021-11', work: 'The Field', value: 25.6, reserve: 20.0, hammer: 25.6 },
      { date: '2022-05', work: 'Versus Medici', value: 35.0, reserve: 25.0, hammer: 35.0 },
      { date: '2022-11', work: 'El Gran Espectaculo', value: 22.0, reserve: 15.0, hammer: 22.0 },
      { date: '2023-05', work: 'Warrior', value: 12.3, reserve: 10.0, hammer: 12.3 },
      { date: '2023-11', work: 'Untitled (Head)', value: 8.5, reserve: 6.0, hammer: 8.5 },
      { date: '2024-02', work: 'Untitled (Devil)', value: 42.4, reserve: 35.0, hammer: 42.4 },
    ],
    recentSales: [
      {
        work: 'Untitled (Devil)',
        house: "Christie's",
        reserve: '$35.0M',
        hammer: '$42.4M',
        premium: '+21%',
        date: 'Feb 2024',
        confidence: 99,
        provenance: [
          { owner: 'Private Collection, New York', period: '1998-2024' },
          { owner: 'Bruno Bischofberger Gallery, Zurich', period: '1984-1998' },
          { owner: 'Acquired directly from the artist', period: '1983-1984' },
        ],
        lotNotes: 'Authenticated by the Authentication Committee of the Estate of Jean-Michel Basquiat',
      },
      {
        work: 'El Gran Espectaculo',
        house: "Sotheby's",
        reserve: '$15.0M',
        hammer: '$18.7M',
        premium: '+25%',
        date: 'Jan 2024',
        confidence: 98,
        provenance: [
          { owner: 'American Private Collection', period: '2005-2024' },
          { owner: 'Gagosian Gallery', period: '2002-2005' },
          { owner: 'Private Collection, Germany', period: '1988-2002' },
        ],
        lotNotes: 'Featured in Whitney Museum exhibition, 1992',
      },
      {
        work: 'Warrior',
        house: "Christie's",
        reserve: '$10.0M',
        hammer: '$12.3M',
        premium: '+23%',
        date: 'Nov 2023',
        confidence: 99,
        provenance: [
          { owner: 'Asian Private Collector', period: '2016-2023' },
          { owner: "Christie's Hong Kong", period: '2016' },
          { owner: 'European Collection', period: '1990-2016' },
        ],
        lotNotes: null,
      },
    ],
    similarArtists: [
      { name: 'Keith Haring', similarity: 91 },
      { name: 'Andy Warhol', similarity: 87 },
      { name: 'Cy Twombly', similarity: 82 },
    ],
  },
  {
    id: 3,
    name: 'Gerhard Richter',
    nationality: 'German',
    born: 1932,
    died: null,
    movements: ['Conceptual Art', 'Photo-Realism'],
    tier: 'Blue-chip',
    careerStage: 'Established',
    medium: 'Paintings, Photography',
    trend: -2.3,
    trendConfidence: 89,
    colorVar: '--accent-warm',
    marketCap: '$3.2B',
    marketCapConfidence: 94,
    avgHammer: '$2.1M',
    avgHammerConfidence: 96,
    recordSale: '$46.3M',
    totalLots: '3,127',
    buyInRate: '18%',
    buyInConfidence: 88,
    sellThrough: '82%',
    sellThroughConfidence: 88,
    overallConfidence: 91,
    priceHistory: [
      { date: '2019-05', work: 'Abstraktes Bild (756)', value: 15.2, reserve: 12.0, hammer: 15.2 },
      { date: '2019-11', work: 'Seestück (Grey)', value: 4.8, reserve: 4.0, hammer: 4.8 },
      { date: '2020-10', work: 'Abstraktes Bild (712)', value: 8.6, reserve: 8.0, hammer: 8.6 },
      { date: '2021-05', work: 'Abstraktes Bild (940)', value: 22.5, reserve: 18.0, hammer: 22.5 },
      { date: '2021-11', work: 'Wolke (Cloud)', value: 12.1, reserve: 10.0, hammer: 12.1 },
      { date: '2022-05', work: 'Abstraktes Bild (648)', value: 28.0, reserve: 20.0, hammer: 28.0 },
      { date: '2022-11', work: 'Kerze (Candle)', value: 9.5, reserve: 8.0, hammer: 9.5 },
      { date: '2023-05', work: 'Abstraktes Bild (899)', value: 14.2, reserve: 12.0, hammer: 14.2 },
      { date: '2023-10', work: 'Seestück', value: 5.7, reserve: 5.0, hammer: 5.7 },
      { date: '2024-02', work: 'Abstraktes Bild (648-3)', value: 18.2, reserve: 15.0, hammer: 18.2 },
    ],
    recentSales: [
      {
        work: 'Abstraktes Bild (648-3)',
        house: 'Phillips',
        reserve: '$15.0M',
        hammer: '$18.2M',
        premium: '+21%',
        date: 'Feb 2024',
        confidence: 97,
        provenance: [
          { owner: 'European Private Collection', period: '2008-2024' },
          { owner: 'Marian Goodman Gallery, New York', period: '2004-2008' },
          { owner: 'Acquired from the artist', period: '2003-2004' },
        ],
        lotNotes: 'Included in catalogue raisonné',
      },
      {
        work: 'Wolke',
        house: "Sotheby's",
        reserve: '$7.0M',
        hammer: '$8.4M',
        premium: '+20%',
        date: 'Dec 2023',
        confidence: 96,
        provenance: [
          { owner: 'Private Collection, Switzerland', period: '1995-2023' },
          { owner: 'Sperone Westwater, New York', period: '1992-1995' },
        ],
        lotNotes: null,
      },
      {
        work: 'Seestück',
        house: "Christie's",
        reserve: '$5.0M',
        hammer: '$5.7M',
        premium: '+14%',
        date: 'Oct 2023',
        confidence: 98,
        provenance: [
          { owner: 'American Private Collection', period: '2000-2023' },
          { owner: 'Anthony d\'Offay Gallery, London', period: '1998-2000' },
        ],
        lotNotes: 'Exhibited at Centre Pompidou retrospective, 2012',
      },
    ],
    similarArtists: [
      { name: 'Sigmar Polke', similarity: 89 },
      { name: 'Anselm Kiefer', similarity: 85 },
      { name: 'Georg Baselitz', similarity: 83 },
    ],
  },
  {
    id: 4,
    name: 'Yoshitomo Nara',
    nationality: 'Japanese',
    born: 1959,
    died: null,
    movements: ['Pop Art', 'Neo-Pop'],
    tier: 'Blue-chip',
    careerStage: 'Established',
    medium: 'Paintings, Sculpture',
    trend: 24.7,
    trendConfidence: 92,
    colorVar: '--accent-deep',
    marketCap: '$890M',
    marketCapConfidence: 93,
    avgHammer: '$1.2M',
    avgHammerConfidence: 95,
    recordSale: '$25.0M',
    totalLots: '1,856',
    buyInRate: '6%',
    buyInConfidence: 96,
    sellThrough: '94%',
    sellThroughConfidence: 96,
    overallConfidence: 94,
    priceHistory: [
      { date: '2019-04', work: 'Miss Moonlight', value: 1.8, reserve: 1.2, hammer: 1.8 },
      { date: '2019-10', work: 'Sleepless Night', value: 2.4, reserve: 1.8, hammer: 2.4 },
      { date: '2020-07', work: 'Little Star', value: 1.2, reserve: 0.8, hammer: 1.2 },
      { date: '2021-03', work: 'Knife Behind Back', value: 25.0, reserve: 12.0, hammer: 25.0 },
      { date: '2021-10', work: 'Princess of Snooze', value: 4.5, reserve: 3.0, hammer: 4.5 },
      { date: '2022-05', work: 'Lookin for Treasure', value: 8.1, reserve: 5.0, hammer: 8.1 },
      { date: '2022-11', work: 'In the Milky Lake', value: 3.8, reserve: 2.5, hammer: 3.8 },
      { date: '2023-04', work: 'Cosmic Girl', value: 6.2, reserve: 4.0, hammer: 6.2 },
      { date: '2023-11', work: 'Sleepless Night', value: 3.4, reserve: 2.5, hammer: 3.4 },
      { date: '2024-01', work: 'Knife Behind Back', value: 25.0, reserve: 12.0, hammer: 25.0 },
    ],
    recentSales: [
      {
        work: 'Knife Behind Back',
        house: "Sotheby's",
        reserve: '$12.0M',
        hammer: '$25.0M',
        premium: '+108%',
        date: 'Jan 2024',
        confidence: 99,
        provenance: [
          { owner: 'Asian Private Collection', period: '2000-2024' },
          { owner: 'Blum & Poe Gallery', period: '1999-2000' },
          { owner: 'Acquired from the artist', period: '1999' },
        ],
        lotNotes: 'Record price for the artist. Major museum exhibition history.',
      },
      {
        work: 'Lookin for the Treasure',
        house: "Christie's",
        reserve: '$5.0M',
        hammer: '$8.1M',
        premium: '+62%',
        date: 'Dec 2023',
        confidence: 98,
        provenance: [
          { owner: 'European Private Collection', period: '2010-2023' },
          { owner: 'Tomio Koyama Gallery, Tokyo', period: '2005-2010' },
        ],
        lotNotes: null,
      },
      {
        work: 'Sleepless Night',
        house: 'Phillips',
        reserve: '$2.5M',
        hammer: '$3.4M',
        premium: '+36%',
        date: 'Nov 2023',
        confidence: 97,
        provenance: [
          { owner: 'American Private Collection', period: '2015-2023' },
          { owner: 'Pace Gallery', period: '2012-2015' },
        ],
        lotNotes: 'Excellent condition, original frame',
      },
    ],
    similarArtists: [
      { name: 'Takashi Murakami', similarity: 93 },
      { name: 'Yayoi Kusama', similarity: 88 },
      { name: 'Mr.', similarity: 79 },
    ],
  },
]

const timePeriods = ['1Y', '3Y', '5Y', 'All']
const movementFilters = ['All Movements', 'Pop Art', 'Neo-Expressionism', 'Minimalism', 'Conceptual Art', 'Street Art', 'Abstract Expressionism']
const tierFilters = ['All Tiers', 'Blue-chip', 'Mid-market', 'Emerging']
const careerFilters = ['All Stages', 'Emerging', 'Mid-career', 'Established', 'Historical']
const mediumFilters = ['All Mediums', 'Paintings', 'Drawings', 'Prints', 'Photography', 'Sculpture', 'Mixed Media']

const emergingArtists = [
  'Jadé Fadojutimi', 'Christina Quarles', 'Amoako Boafo', 'Matthew Wong',
  'Avery Singer', 'Flora Yukhnovich', 'Issy Wood', 'Lucy Bull'
]

function ConfidenceBadge({ value }: { value: number }) {
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-page text-text-tertiary">
      {value}% confidence
    </span>
  )
}

type Sale = {
  work: string
  house: string
  reserve: string
  hammer: string
  premium: string
  date: string
  confidence: number
  provenance: { owner: string; period: string }[]
  lotNotes: string | null
}

function SaleRow({ sale, artistColor }: { sale: Sale; artistColor: string }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <tr
        className="border-b border-border-subtle cursor-pointer hover:bg-page/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="py-3 text-sm">
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-text-tertiary transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {sale.work}
          </div>
        </td>
        <td className="py-3 text-sm text-text-secondary">{sale.house}</td>
        <td className="py-3 text-sm text-right text-text-tertiary tabular-nums">{sale.reserve}</td>
        <td className="py-3 text-sm text-right font-medium tabular-nums">{sale.hammer}</td>
        <td className="py-3 text-center">
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ background: 'var(--positive)', color: 'white' }}
          >
            {sale.premium}
          </span>
        </td>
        <td className="py-3 text-center">
          <ConfidenceBadge value={sale.confidence} />
        </td>
        <td className="py-3 text-sm text-right text-text-tertiary">{sale.date}</td>
      </tr>

      {/* Expanded Provenance Details */}
      <AnimatePresence>
        {expanded && (
          <motion.tr
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <td colSpan={7} className="bg-page/30">
              <div className="px-8 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Provenance */}
                  <div>
                    <h5 className="text-xs font-medium text-text-secondary mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Provenance
                    </h5>
                    <div className="space-y-2">
                      {sale.provenance.map((p, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-3 text-sm"
                        >
                          <div
                            className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                            style={{ background: artistColor, opacity: 1 - i * 0.25 }}
                          />
                          <div>
                            <p className="text-text-primary">{p.owner}</p>
                            <p className="text-xs text-text-tertiary">{p.period}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sale Details */}
                  <div>
                    <h5 className="text-xs font-medium text-text-secondary mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Sale Details
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-text-tertiary">Reserve Price</span>
                        <span>{sale.reserve}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-tertiary">Hammer Price</span>
                        <span className="font-medium">{sale.hammer}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-tertiary">Premium over Reserve</span>
                        <span style={{ color: 'var(--positive)' }}>{sale.premium}</span>
                      </div>
                      {sale.lotNotes && (
                        <div className="pt-2 mt-2 border-t border-border-subtle">
                          <p className="text-xs text-text-tertiary">
                            <span className="font-medium">Note:</span> {sale.lotNotes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  )
}

export function ArtistIntelligence() {
  const [selectedArtist, setSelectedArtist] = useState(artists[0])
  const [timePeriod, setTimePeriod] = useState('5Y')
  const [movementFilter, setMovementFilter] = useState('All Movements')
  const [tierFilter, setTierFilter] = useState('All Tiers')
  const [careerFilter, setCareerFilter] = useState('All Stages')
  const [mediumFilter, setMediumFilter] = useState('All Mediums')

  // Get computed color for charts (Recharts needs hex values, not CSS vars)
  const artistColor = useComputedColor(selectedArtist.colorVar)
  const textTertiary = useComputedColor('--text-tertiary')

  return (
    <section id="artist-intelligence" className="py-24 px-6 bg-card">
      <div className="max-w-7xl mx-auto">
        {/* Header - left aligned */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mb-2">
              Artist Intelligence
            </h2>
            <p className="text-text-secondary max-w-xl">
              Actionable intelligence on 180,000+ artists. Identify opportunities,
              track positioning, and act before the market moves.
            </p>
          </div>

          {/* Time period filter - hidden on mobile */}
          <div className="hidden md:flex items-center gap-2">
            {timePeriods.map((period) => (
              <button
                key={period}
                onClick={() => setTimePeriod(period)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  timePeriod === period
                    ? 'bg-text-primary text-white'
                    : 'bg-transparent border border-border text-text-secondary hover:border-border-hover'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        {/* AI Signal - Condensed */}
        <div className="card p-4 mb-8 flex items-center gap-4 border-l-2" style={{ borderLeftColor: 'var(--accent-primary)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-primary)', color: 'white' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm">
              <span className="font-medium">Market Signal:</span>{' '}
              <span className="text-text-secondary">Emerging female artists outperforming market 3.2x. Key movers: Amoako Boafo, Flora Yukhnovich, Anna Weyant—institutional positioning detected.</span>
            </p>
          </div>
          <Link href="/get-started" className="text-xs text-accent-primary hover:underline flex-shrink-0">
            See full analysis →
          </Link>
        </div>

        {/* Search bar - left aligned */}
        <div className="max-w-xl mb-6">
          <div className="relative">
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by artist name..."
              className="w-full pl-12 pr-4 py-4 rounded-full border border-border bg-page text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-border-hover focus:ring-2 focus:ring-accent-primary/10 transition-all"
            />
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-3 mb-10">
          {/* Movement filter */}
          <select
            value={movementFilter}
            onChange={(e) => setMovementFilter(e.target.value)}
            className={`px-4 py-2 rounded-full text-sm border bg-transparent cursor-pointer transition-all ${
              movementFilter !== 'All Movements' ? 'border-accent-primary text-accent-primary' : 'border-border text-text-secondary'
            }`}
          >
            {movementFilters.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          {/* Tier filter */}
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className={`px-4 py-2 rounded-full text-sm border bg-transparent cursor-pointer transition-all ${
              tierFilter !== 'All Tiers' ? 'border-accent-primary text-accent-primary' : 'border-border text-text-secondary'
            }`}
          >
            {tierFilters.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {/* Career stage filter */}
          <select
            value={careerFilter}
            onChange={(e) => setCareerFilter(e.target.value)}
            className={`px-4 py-2 rounded-full text-sm border bg-transparent cursor-pointer transition-all ${
              careerFilter !== 'All Stages' ? 'border-accent-primary text-accent-primary' : 'border-border text-text-secondary'
            }`}
          >
            {careerFilters.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {/* Medium filter */}
          <select
            value={mediumFilter}
            onChange={(e) => setMediumFilter(e.target.value)}
            className={`px-4 py-2 rounded-full text-sm border bg-transparent cursor-pointer transition-all ${
              mediumFilter !== 'All Mediums' ? 'border-accent-primary text-accent-primary' : 'border-border text-text-secondary'
            }`}
          >
            {mediumFilters.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* Artist card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedArtist.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="bg-page rounded-2xl border border-border p-8 mb-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Left: Artist info and metrics */}
              <div>
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start gap-4">
                    {/* Artist avatar */}
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center font-medium text-white text-xl flex-shrink-0 border-2"
                      style={{
                        background: `var(${selectedArtist.colorVar})`,
                        borderColor: `var(${selectedArtist.colorVar})`,
                      }}
                    >
                      {getArtistInitials(selectedArtist.name)}
                    </div>
                    <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-display text-3xl font-light">
                        {selectedArtist.name}
                      </h3>
                      <span
                        className="text-xs px-2 py-1 rounded-full"
                        style={{ background: 'var(--positive)', color: 'white' }}
                      >
                        {selectedArtist.overallConfidence}% confidence
                      </span>
                    </div>
                    <p className="text-text-secondary">
                      {selectedArtist.nationality}, {selectedArtist.born}{selectedArtist.died ? `–${selectedArtist.died}` : ''}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {/* Tier badge */}
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        selectedArtist.tier === 'Blue-chip'
                          ? 'bg-accent-primary/10 text-accent-primary'
                          : selectedArtist.tier === 'Mid-market'
                          ? 'bg-accent-warm/10 text-accent-warm'
                          : 'bg-positive/10 text-positive'
                      }`}>
                        {selectedArtist.tier}
                      </span>
                      {/* Career stage badge */}
                      <span className="text-xs px-2 py-1 rounded-full bg-card border border-border-subtle text-text-tertiary">
                        {selectedArtist.careerStage}
                      </span>
                      {/* Movement badges */}
                      {selectedArtist.movements.map((mov) => (
                        <span key={mov} className="text-xs px-2 py-1 rounded-full border border-border text-text-secondary">
                          {mov}
                        </span>
                      ))}
                    </div>
                    <p className="text-text-tertiary text-sm mt-2">
                      {selectedArtist.medium}
                    </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className="text-xl font-medium tabular-nums"
                      style={{ color: selectedArtist.trend >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                    >
                      {selectedArtist.trend >= 0 ? '+' : ''}{selectedArtist.trend}%
                    </span>
                    <p className="text-xs text-text-muted mt-1">
                      {selectedArtist.trendConfidence}% conf.
                    </p>
                  </div>
                </div>

                {/* Metrics grid - now with confidence */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Market Cap', value: selectedArtist.marketCap, confidence: selectedArtist.marketCapConfidence },
                    { label: 'Avg Hammer', value: selectedArtist.avgHammer, confidence: selectedArtist.avgHammerConfidence },
                    { label: 'Record Sale', value: selectedArtist.recordSale, confidence: 99 },
                    { label: 'Total Lots', value: selectedArtist.totalLots, confidence: 100 },
                    { label: 'Buy-in Rate', value: selectedArtist.buyInRate, confidence: selectedArtist.buyInConfidence },
                    { label: 'Sell-through', value: selectedArtist.sellThrough, confidence: selectedArtist.sellThroughConfidence },
                  ].map((metric) => (
                    <div key={metric.label} className="bg-card rounded-xl p-3 border border-border-subtle">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-text-tertiary text-xs">{metric.label}</p>
                        <ConfidenceBadge value={metric.confidence} />
                      </div>
                      <p className="text-lg font-display font-light tabular-nums">{metric.value}</p>
                    </div>
                  ))}
                </div>

                {/* CTAs */}
                <div className="flex flex-wrap gap-3 mt-6">
                  <Link
                    href={`/app/artists/${selectedArtist.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="pill text-sm"
                    style={{ background: 'var(--accent-primary)', color: 'white' }}
                  >
                    Full profile
                  </Link>
                  <Link
                    href="/get-started"
                    className="pill text-sm flex items-center gap-2"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--border-standard)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    Add to watchlist
                  </Link>
                  <Link
                    href="/get-started"
                    className="pill text-sm flex items-center gap-2"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--border-standard)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Get alerts
                  </Link>
                </div>
              </div>

              {/* Right: Chart with individual works */}
              <div className="min-h-[280px]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-text-tertiary">Price History by Work</p>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ background: `var(${selectedArtist.colorVar})` }} />
                      Hammer
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full border border-text-tertiary" />
                      Reserve
                    </span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: textTertiary, fontSize: 10 }}
                      tickFormatter={(val) => val.split('-')[0]}
                    />
                    <YAxis
                      dataKey="value"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: textTertiary, fontSize: 10 }}
                      tickFormatter={(val) => `$${val}M`}
                      domain={['dataMin - 1', 'dataMax + 2']}
                    />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload
                          return (
                            <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                              <p className="font-medium text-sm mb-1">{data.work}</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                <span className="text-text-tertiary">Reserve:</span>
                                <span className="text-right">${data.reserve}M</span>
                                <span className="text-text-tertiary">Hammer:</span>
                                <span className="text-right font-medium">${data.hammer}M</span>
                                <span className="text-text-tertiary">Premium:</span>
                                <span className="text-right" style={{ color: 'var(--positive)' }}>
                                  +{Math.round(((data.hammer - data.reserve) / data.reserve) * 100)}%
                                </span>
                              </div>
                              <p className="text-xs text-text-muted mt-2">{data.date}</p>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    {/* Reserve price line (hollow circles) */}
                    <Scatter
                      data={selectedArtist.priceHistory}
                      dataKey="reserve"
                      fill="transparent"
                      stroke={textTertiary}
                      strokeWidth={1.5}
                      shape="circle"
                    >
                      {selectedArtist.priceHistory.map((entry, index) => (
                        <Cell key={`reserve-${index}`} />
                      ))}
                    </Scatter>
                    {/* Hammer price (filled circles) */}
                    <Scatter
                      data={selectedArtist.priceHistory}
                      fill={artistColor}
                    >
                      {selectedArtist.priceHistory.map((entry, index) => (
                        <Cell key={`hammer-${index}`} fill={artistColor} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Sales Table with Reserve vs Hammer */}
            <div className="border-t border-border pt-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-text-secondary">Recent Sales</h4>
                <p className="text-xs text-text-tertiary">Click row to view provenance</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-subtle">
                      <th className="text-left py-2 text-xs font-medium text-text-tertiary">Work</th>
                      <th className="text-left py-2 text-xs font-medium text-text-tertiary">House</th>
                      <th className="text-right py-2 text-xs font-medium text-text-tertiary">Reserve</th>
                      <th className="text-right py-2 text-xs font-medium text-text-tertiary">Hammer</th>
                      <th className="text-center py-2 text-xs font-medium text-text-tertiary">Premium</th>
                      <th className="text-center py-2 text-xs font-medium text-text-tertiary">Conf.</th>
                      <th className="text-right py-2 text-xs font-medium text-text-tertiary">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedArtist.recentSales.map((sale, i) => (
                      <SaleRow key={i} sale={sale} artistColor={artistColor} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Similar Artists */}
            <div className="border-t border-border pt-6">
              <h4 className="text-sm font-medium text-text-secondary mb-3">Comparable Artists</h4>
              <div className="flex flex-wrap gap-2">
                {selectedArtist.similarArtists.map((artist) => (
                  <Link
                    key={artist.name}
                    href="/get-started"
                    className="group px-4 py-2 rounded-full text-sm border border-border text-text-secondary hover:border-border-hover cursor-pointer transition-all flex items-center gap-2"
                  >
                    {artist.name}
                    <span className="text-xs text-text-muted group-hover:text-text-tertiary">
                      {artist.similarity}% similar
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Artist selector pills */}
        <div className="flex flex-wrap gap-3 mb-12">
          {artists.map((artist) => (
            <button
              key={artist.id}
              onClick={() => setSelectedArtist(artist)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                selectedArtist.id === artist.id
                  ? 'text-white'
                  : 'bg-page border border-border text-text-secondary hover:border-border-hover'
              }`}
              style={
                selectedArtist.id === artist.id
                  ? { background: `var(${artist.colorVar})` }
                  : {}
              }
            >
              {artist.name}
              {selectedArtist.id !== artist.id && (
                <span className="text-xs text-text-muted">{artist.overallConfidence}%</span>
              )}
            </button>
          ))}
        </div>

        {/* Trending/Emerging Artists */}
        <div className="border-t border-border pt-8">
          <p className="text-text-secondary text-sm mb-4">Trending emerging artists</p>
          <div className="flex flex-wrap gap-2 mb-8">
            {emergingArtists.map((artist) => (
              <Link
                key={artist}
                href={`/app/artists/${artist.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-4 py-2 rounded-full text-sm border border-border text-text-secondary hover:border-accent-primary hover:text-accent-primary cursor-pointer transition-all"
              >
                {artist}
              </Link>
            ))}
          </div>

          {/* Section CTAs */}
          <div className="flex flex-wrap gap-4">
            <Link
              href="/app/artists"
              className="pill"
              style={{ background: 'var(--accent-primary)', color: 'white' }}
            >
              Explore artist intelligence
            </Link>
            <Link
              href="/get-started"
              className="pill flex items-center gap-2"
              style={{
                background: 'transparent',
                border: '1px solid var(--border-standard)',
                color: 'var(--text-primary)'
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Request access
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}
